// Eligibility Service
//
// The single entry point the UI calls to verify insurance coverage. It hides
// which side of the mock boundary we are on:
//
//   mock mode      → StediMockService, parsed and merged in the browser
//   production     → POST /api/verify-eligibility (Phase 2), which does the same
//                    work server-side with real credentials and persists it
//
// Flipping REACT_APP_USE_STEDI_MOCK to 'false' is the Phase 5 cutover. Nothing
// upstream of this file should know the difference.

import {
  CarveOutInfo,
  DEFAULT_SERVICE_TYPE_CODE,
  ELIGIBILITY_CACHE_HOURS,
  EligibilityCheck,
  EligibilityCheckResult,
  EligibilityRequest,
  EligibilityResponse,
  ServiceTypeCode,
  VerifyEligibilityInput,
} from '../types/eligibility';
import {
  ParsedEligibility,
  mergeCarveOutResult,
  parseEligibilityResponse,
} from '../utils/eligibilityParser';
import { eligibilityKeyContext, encryptEligibilityResponse } from '../utils/encryption';
import { StediMockService } from './StediMockService';

/**
 * Known MBHO names → trading partner IDs, for carve-outs where the payer names
 * the administrator but returns a member ID instead of a payer ID.
 * TODO (Phase 2): resolve against the `payers` collection so this list stops
 * being a maintenance burden. It only covers what the mock exercises today.
 */
const MBHO_NAME_TO_PAYER_ID: Array<{ match: string; payerId: string }> = [
  { match: 'CARELON', payerId: 'CARBH' },
  { match: 'MAGELLAN', payerId: '01260' },
];

interface CachedCheck {
  check: EligibilityCheck;
  expiresAt: number;
}

/**
 * In-memory cache for mock mode only. Production caching lives in the endpoint,
 * backed by `eligibility_checks`, because that is where the billable call is
 * made — a per-tab cache would not stop duplicate charges.
 */
const mockCache = new Map<string, CachedCheck>();

function cacheKey(input: VerifyEligibilityInput, stc: ServiceTypeCode): string {
  return `${input.patientId}|${input.payerId}|${stc}`;
}

/** X12 requires a 9-character control number. */
function controlNumber(): string {
  return String(Math.floor(100000000 + Math.random() * 900000000));
}

/** ISO (YYYY-MM-DD) → X12 (YYYYMMDD). Passes through anything already stripped. */
export function toX12Date(iso: string): string {
  return (iso || '').replace(/-/g, '');
}

function buildRequest(
  input: VerifyEligibilityInput,
  stc: ServiceTypeCode,
  overrides?: { tradingPartnerServiceId?: string; memberId?: string }
): EligibilityRequest {
  return {
    controlNumber: controlNumber(),
    tradingPartnerServiceId: overrides?.tradingPartnerServiceId || input.stediPayerId,
    provider: {
      organizationName: input.providerOrganizationName,
      npi: input.providerNpi,
    },
    subscriber: {
      memberId: overrides?.memberId || input.memberId,
      firstName: input.firstName,
      lastName: input.lastName,
      dateOfBirth: toX12Date(input.dateOfBirth),
    },
    encounter: {
      serviceTypeCodes: [stc],
      dateOfService: toX12Date(input.dateOfService || new Date().toISOString().slice(0, 10)),
    },
  };
}

/** Which payer ID to use for the MBHO second pass, if we can determine one. */
function resolveCarveOutPayerId(carveOut: CarveOutInfo): string | undefined {
  if (carveOut.payerIdentifier) return carveOut.payerIdentifier;

  const name = (carveOut.entityName || '').toUpperCase();
  return MBHO_NAME_TO_PAYER_ID.find((entry) => name.includes(entry.match))?.payerId;
}

export class EligibilityService {
  /**
   * Mock unless explicitly switched off, so a missing env var can never cause a
   * real (billable, PHI-bearing) call by accident.
   */
  static isMockMode(): boolean {
    return process.env.REACT_APP_USE_STEDI_MOCK !== 'false';
  }

  static async verify(input: VerifyEligibilityInput): Promise<EligibilityCheckResult> {
    const stc = input.serviceTypeCode || DEFAULT_SERVICE_TYPE_CODE;

    return this.isMockMode()
      ? this.verifyWithMock(input, stc)
      : this.verifyWithEndpoint(input, stc);
  }

  // ─── Mock path ──────────────────────────────────────────────────────

  private static async verifyWithMock(
    input: VerifyEligibilityInput,
    stc: ServiceTypeCode
  ): Promise<EligibilityCheckResult> {
    const key = cacheKey(input, stc);

    if (!input.forceRefresh) {
      const cached = mockCache.get(key);
      if (cached && cached.expiresAt > Date.now()) {
        return { check: cached.check, fromCache: true };
      }
    }

    const primaryRequest = buildRequest(input, stc);
    const primaryResponse = await StediMockService.checkEligibility(primaryRequest);
    let parsed = parseEligibilityResponse(primaryResponse, stc);

    let carveOutResponse: EligibilityResponse | undefined;

    // Second pass: the primary payer told us someone else owns these benefits.
    if (parsed.carveOut.detected) {
      const carveOutPayerId = resolveCarveOutPayerId(parsed.carveOut);

      if (!carveOutPayerId) {
        parsed = {
          ...parsed,
          carveOut: {
            ...parsed.carveOut,
            secondPassError:
              'Carve-out administrator could not be matched to a payer — verify manually.',
          },
        };
      } else {
        try {
          const secondRequest = buildRequest(input, stc, {
            tradingPartnerServiceId: carveOutPayerId,
            memberId: parsed.carveOut.memberId,
          });
          carveOutResponse = await StediMockService.checkEligibility(secondRequest);
          const secondParsed = parseEligibilityResponse(carveOutResponse, stc);
          parsed = mergeCarveOutResult(parsed, secondParsed, {
            ...parsed.carveOut,
            resolvedPayerId: carveOutPayerId,
          });
        } catch (error) {
          // A failed second pass must not discard the primary result — the UI
          // shows what we know plus an explicit warning.
          parsed = {
            ...parsed,
            carveOut: {
              ...parsed.carveOut,
              secondPassError:
                error instanceof Error ? error.message : 'Carve-out check failed',
            },
          };
        }
      }
    }

    const check = await this.buildCheck(input, stc, parsed, primaryResponse, carveOutResponse);

    // Errors are not cached — a transient payer failure should not block a retry.
    if (check.status !== 'error') {
      mockCache.set(key, { check, expiresAt: check.expiresAt.getTime() });
    }

    return { check, fromCache: false };
  }

  // ─── Production path (endpoint lands in Phase 2) ────────────────────

  private static async verifyWithEndpoint(
    input: VerifyEligibilityInput,
    stc: ServiceTypeCode
  ): Promise<EligibilityCheckResult> {
    const { getAuth } = await import('firebase/auth');
    const token = await getAuth().currentUser?.getIdToken();

    if (!token) {
      throw new Error('You must be signed in to verify insurance coverage.');
    }

    const response = await fetch('/api/verify-eligibility', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ ...input, serviceTypeCode: stc }),
    });

    if (!response.ok) {
      const detail = await response.text().catch(() => '');
      throw new Error(
        `Eligibility check failed (${response.status}). ${detail || 'Please try again.'}`
      );
    }

    const body = await response.json();

    // The endpoint returns serialized dates; revive them for the UI.
    return {
      fromCache: Boolean(body.fromCache),
      check: {
        ...body.check,
        checkedAt: new Date(body.check.checkedAt),
        expiresAt: new Date(body.check.expiresAt),
      },
    };
  }

  // ─── Shared ─────────────────────────────────────────────────────────

  private static async buildCheck(
    input: VerifyEligibilityInput,
    stc: ServiceTypeCode,
    parsed: ParsedEligibility,
    primaryResponse: EligibilityResponse,
    carveOutResponse?: EligibilityResponse
  ): Promise<EligibilityCheck> {
    const checkedAt = new Date();
    const expiresAt = new Date(checkedAt.getTime() + ELIGIBILITY_CACHE_HOURS * 60 * 60 * 1000);
    const keyContext = eligibilityKeyContext(input.practiceId);

    return {
      id: `mock-${checkedAt.getTime()}`,
      patientId: input.patientId,
      providerId: input.providerId,
      practiceId: input.practiceId,
      payerId: input.payerId,
      payerName: input.payerName,
      stediPayerId: input.stediPayerId,
      serviceTypeCode: stc,
      status: parsed.status,
      statusReason: parsed.statusReason,
      benefits: parsed.benefits,
      carveOut: parsed.carveOut,
      rawResponse: await encryptEligibilityResponse(primaryResponse, keyContext),
      rawCarveOutResponse: carveOutResponse
        ? await encryptEligibilityResponse(carveOutResponse, keyContext)
        : undefined,
      rawResponseEncrypted: true,
      source: 'mock',
      checkedAt,
      checkedBy: input.providerId,
      expiresAt,
      controlNumber: primaryResponse.controlNumber,
      traceId: primaryResponse.meta?.traceId,
      errors: parsed.errors,
    };
  }

  /** Clear the mock cache — for the dev scenario switcher and tests. */
  static clearMockCache(): void {
    mockCache.clear();
  }
}
